import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Flower2, Menu, X } from 'lucide-react';
import emailjs from '@emailjs/browser';
import Shop from './Shop';   

function Home() {
  const [isWeddingsOpen, setIsWeddingsOpen] = useState(false);

  return (
    <>
      {/* Hero */}
      <section id="home" className="min-h-screen flex items-center justify-center text-center overflow-hidden bg-cream relative">
        <div className="relative px-6 max-w-4xl mx-auto flex flex-col items-center pt-safe pb-12">
          <img 
            src="/static/tessaflora_image_nowords.webp" 
            alt="Tessa Flora" 
            className="h-52 md:h-64 lg:h-72 w-auto mb-8 opacity-95"
          />

          <h1 
            className="text-6xl md:text-7xl mb-6 tracking-tight" 
            style={{ fontFamily: "'Pinyon Script', cursive", color: '#599FBC' }}
          >
            Made To Be Adored
          </h1>
          
          <p className="text-2xl md:text-3xl mb-10 text-gray-700">
            Fresh, Seasonal & Thoughtful
          </p>
          
          <Link 
            to="/shop" 
            className="inline-block bg-primary text-white hover:bg-primary-dark text-xl font-medium px-10 py-4 rounded-full transition"
          >
            Explore Our Collection
          </Link>
        </div>
      </section>

      {/* Three feature tiles */}
      <section id="explore" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xl md:text-2xl mb-12 text-gray-600">
            Fresh flowers delivered Friday and Saturday
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* 1. Weddings & Events */}
            <div className="bg-cream rounded-3xl overflow-hidden hover:shadow-2xl transition group flex flex-col">
              <div className="h-80 bg-gray-100 overflow-hidden">
                <img 
                  src="/static/wedding_cover.webp" 
                  alt="Weddings and Special Events"
                  className="w-full h-full object-cover group-hover:scale-105 transition"
                />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <h3 className="text-2xl font-medium mb-6">Weddings & Special Events</h3>
                <button
                  onClick={() => setIsWeddingsOpen(true)}
                  className="mt-auto w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-2xl transition font-medium"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* 2. Shop Now */}
            <div className="bg-cream rounded-3xl overflow-hidden hover:shadow-2xl transition group flex flex-col">
              <div className="h-80 bg-gray-100 overflow-hidden">
                <img 
                  src="/static/portfolio/portfolio_11.webp" 
                  alt="Shop Now"
                  className="w-full h-full object-cover group-hover:scale-105 transition"
                />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <h3 className="text-2xl font-medium mb-6">Shop Now</h3>
                <Link
                  to="/shop"
                  className="mt-auto block w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-2xl transition text-center font-medium"
                >
                  Browse Collection
                </Link>
              </div>
            </div>

            {/* 3. Portfolio */}
            <div className="bg-cream rounded-3xl overflow-hidden hover:shadow-2xl transition group flex flex-col">
              <div className="h-80 bg-gray-100 overflow-hidden">
                <img 
                  src="/static/portfolio/portfolio_9.webp" 
                  alt="Portfolio"
                  className="w-full h-full object-cover group-hover:scale-105 transition"
                />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <h3 className="text-2xl font-medium mb-6">Portfolio</h3>
                <Link
                  to="/portfolio"
                  className="mt-auto block w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-2xl transition text-center font-medium"
                >
                  View Work
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weddings Modal */}
      {isWeddingsOpen && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setIsWeddingsOpen(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setIsWeddingsOpen(false)}
                className="absolute top-4 right-4 z-20 bg-white/90 hover:bg-white text-gray-700 w-10 h-10 rounded-full flex items-center justify-center text-2xl shadow-md"
              >
                ✕
              </button>

              <img 
                src="/static/wedding_modal_2.webp" 
                alt="Weddings and Special Events"
                className="w-full h-64 object-cover rounded-t-3xl"
              />

              <div className="p-8 text-center">
                <h2 
                  className="text-4xl mb-4"
                  style={{ fontFamily: "'Pinyon Script', cursive", color: '#599FBC' }}
                >
                  Weddings & Special Events
                </h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Tessa Flora can create thoughtful, seasonal and beautiful bridal bouquets, table arrangements and wired work for your event or wedding to be extra special. Get in touch to chat about your vision and colour palette.
                </p>
                <Link
                  to="/contact"
                  onClick={() => setIsWeddingsOpen(false)}
                  className="inline-block bg-primary hover:bg-primary-dark text-white px-10 py-4 rounded-full transition font-medium"
                >
                  Enquire Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function About() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-20">
      <h1 
        className="text-5xl font-serif text-center mb-12" 
        style={{ 
          fontFamily: "'Pinyon Script', cursive", 
          color: '#599FBC' 
        }}
      >
        About Tessa Flora
      </h1>

      <div className="prose prose-lg text-gray-700 leading-relaxed max-w-none space-y-8">
        <p className="text-xl">Hi, welcome to Tessa Flora!</p>
        
        <p>
          My name is Tess, an Orange local and a lover of beautiful flowers. Tessa Flora is a small retail and event studio, bringing you and your loved ones fresh, seasonal flowers on Fridays & Saturdays.
        </p>

        <p>
          The name Tessa Flora was thought of by my brother Patrick, which is really special as he has been one of my biggest encouragers and has been so incredibly supportive. There were no other names or doubts — as soon as he suggested it, it felt perfect. 
        </p>

        <p>
          As a trained florist, having completed my studies eight years ago and then spending five lovely years with Botanica Flora, I’m excited to begin this new and exciting chapter.
        </p>

        <p>
          At Botanica Flora, I really appreciated the care, thought and attention to detail we put into each arrangement. At Tessa Flora, the same philosophy applies, ensuring each order is thoughtfully created, wrapped in beautiful embossed paper, tied with complementing ribbons and then hand-delivered with care. 
        </p>

        <p>
I love choosing fresh, seasonal flowers for you, benefiting both the customer and the environment. Flowers are naturally at their peak, meaning they are fresher, stronger, and often have a longer vase life. Buying seasonally also supports local growers, and I love seeing the changing flower availability throughout the year. Hydrangea and tulips are my favourite seasons! 
        </p>

        <p>
Flower conditioning is an essential step that I take pride in doing. This ensures each flower is cared for properly and looking their best. When designing, my style approach is romantic and garden-inspired, working with a smaller selection of seasonal varieties to allow each one to be appreciated and shine in abundance!
        </p>

        <p>
The bouquets and vase arrangements I create for you are made to be extra special and adored.
        </p>

        <p className="font-medium text-lg mt-12">
          Thank you for visiting Tessa Flora and I do hope I have the opportunity to spoil you or your loved one soon.
        </p>
        
        <p className="text-primary font-medium text-xl">Tessa x</p>
      </div>
    

      {/* Testimonials */}
      <section className="py-20 bg-cream mt-16 rounded-3xl">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 
            className="text-5xl font-serif mb-12" 
            style={{ 
              fontFamily: "'Pinyon Script', cursive", 
              color: '#599FBC' 
            }}
          >
            What Our Customers Say
          </h2>
          
          <div className="space-y-12">
            <div>
              <p className="text-xl italic text-gray-700">"The most beautiful bouquet I've ever received. The attention to detail and freshness was outstanding. Highly recommend!"</p>
              <p className="mt-4 font-medium">— Beatrice S, Spring Hill</p>
            </div>
            
            <div>
              <p className="text-xl italic text-gray-700">"Tessa Flora made our event look amazing with the flowers. Everyone commented on how stunning they were. Thank you!"</p>
              <p className="mt-4 font-medium">— Pauline H, Orange</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');

    const form = e.currentTarget;

    try {
      await emailjs.sendForm(
        'service_t97fblu',           // Your Service ID
        'template_aag7trj',          // Your Template ID
        form,
        'VdV9FEn45uyhJhpMf'          // Your Public Key
      );

      setStatus('success');
      form.reset();
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-20">
<h1 className="text-5xl font-serif text-center mb-10" 
    style={{ 
      fontFamily: "'Pinyon Script', cursive", 
      color: '#599FBC' 
    }}>
  Get In Touch
</h1>
      
      <div className="grid md:grid-cols-2 gap-12">
        <div>
<h2 
  className="text-5xl font-serif mb-12 text-center" 
  style={{ 
    fontFamily: "'Pinyon Script', cursive", 
    color: '#599FBC' 
  }}
>
 Contact Information
</h2>
          <p className="mb-4"><strong>Phone:</strong> 0421 829 998</p>
          <p className="mb-4"><strong>Email:</strong> tessafloraorange@gmail.com</p>
        </div>

        <div>
     <h2 
  className="text-5xl font-serif mb-12 text-center" 
  style={{ 
    fontFamily: "'Pinyon Script', cursive", 
    color: '#599FBC' 
  }}
>
  Send Us a Message
</h2>
          
          <form onSubmit={sendEmail} className="space-y-6">
            <input type="text" name="user_name" placeholder="Your Name" required 
                   className="w-full p-4 border rounded-xl" />
            <input type="email" name="user_email" placeholder="Email Address" required 
                   className="w-full p-4 border rounded-xl" />
            <input type="phone" name="user_phone" placeholder="Phone Number" required 
                   className="w-full p-4 border rounded-xl" />
            <textarea name="message" placeholder="Your Message" rows={6} required 
                      className="w-full p-4 border rounded-xl"></textarea>
            
            <button 
              type="submit" 
              disabled={status === 'sending'}
              className="bg-primary hover:bg-primary-dark disabled:bg-gray-400 text-white px-10 py-4 rounded-full font-medium w-full md:w-auto transition"
            >
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </button>

            {status === 'success' && (
              <p className="text-green-600 font-medium">✅ Message sent successfully! We'll get back to you soon.</p>
            )}
            {status === 'error' && (
              <p className="text-red-600 font-medium">❌ Something went wrong. Please try again.</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

function Portfolio() {
  const images = Array.from({ length: 12 }, (_, i) => `/static/portfolio/portfolio_${i + 1}.webp`);

  return (
    <div className="max-w-6xl mx-auto px-6 py-20">
      <h1 
        className="text-5xl text-center mb-12"
        style={{ fontFamily: "'Pinyon Script', cursive", color: '#599FBC' }}
      >
        Portfolio
      </h1>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
        {images.map((src) => (
          <div key={src} className="overflow-hidden rounded-3xl bg-cream">
            <img
              src={src}
              alt="Tessa Flora portfolio"
              className="w-full h-80 object-cover hover:scale-105 transition duration-500"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ThankYou() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center text-center px-6 py-20 bg-cream">
      <div className="max-w-lg">
        <div className="text-8xl mb-6">🌸</div>
        <h1 
          className="text-5xl font-serif mb-6"
          style={{ fontFamily: "'Pinyon Script', cursive", color: '#599FBC' }}
        >
          Thank You!
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Your order has been received. We’ll be in touch soon to confirm the details.
        </p>
        <Link 
          to="/" 
          className="inline-block bg-primary hover:bg-primary-dark text-white px-10 py-4 rounded-full transition"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Router>
      <div className="min-h-screen bg-cream text-gray-800 font-sans">
       
        {/* Navbar */}
<nav className="bg-white shadow-sm sticky top-0 z-50">
  <div className="max-w-6xl mx-auto px-6 py-6">
    <div className="flex justify-between items-center">
      
      {/* Left spacer - same width as hamburger */}
      <div className="w-10 md:hidden"></div>

      {/* Logo + Title - Centered */}
      <Link to="/" className="flex items-center gap-4 hover:opacity-90 transition">
{/*         <img 
          src="/static/tessaflora_image_nowords.webp" 
          alt="Tessa Flora" 
          className="h-16 w-auto" 
        /> */}
        <span 
          className="text-5xl font-serif tracking-tight text-primary" 
          style={{ fontFamily: "'Pinyon Script', cursive" }}
        >
          Tessa Flora
        </span>
      </Link>

      {/* Right side - Hamburger + Desktop Menu */}
      <div className="flex items-center gap-4">
        <div className="hidden md:flex gap-8 text-lg">
          <Link to="/" className="hover:text-primary transition">Home</Link>
          <Link to="/shop" className="hover:text-primary transition">Shop</Link>
          <Link to="/about" className="hover:text-primary transition">About</Link>
          <Link to="/portfolio" className="hover:text-primary transition">Portfolio</Link>
          <Link to="/contact" className="hover:text-primary transition">Contact</Link>
        </div>

        <Link to="/shop" className="hidden md:block">
          <button className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full flex items-center gap-2 transition font-medium text-lg">
            <Flower2 size={22} /> Shop Now
          </button>
        </Link>

        {/* Hamburger Button */}
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden text-3xl text-gray-700"
        >
          {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
        </button>
      </div>
    </div>

    {/* Mobile Menu */}
    {isMenuOpen && (
      <div className="md:hidden bg-white border-t py-8 px-6 text-center space-y-6 text-lg mt-4">
        <Link to="/" onClick={() => setIsMenuOpen(false)} className="block hover:text-primary">Home</Link>
        <Link to="/shop" onClick={() => setIsMenuOpen(false)} className="block hover:text-primary">Shop</Link>
        <Link to="/about" onClick={() => setIsMenuOpen(false)} className="block hover:text-primary">About</Link>
        <Link to="/portfolio" onClick={() => setIsMenuOpen(false)} className="block hover:text-primary">Portfolio</Link>
        <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="block hover:text-primary">Contact</Link>
        
        <Link to="/shop" onClick={() => setIsMenuOpen(false)}>
          <button className="w-full mt-6 bg-primary hover:bg-primary-dark text-white py-4 rounded-full font-medium text-lg">
            <Flower2 size={22} className="inline mr-2" /> Shop Now
          </button>
        </Link>
      </div>
    )}
  </div>
</nav>

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
         <Route path="/shop" element={<Shop />} /> 
         <Route path="/thank-you" element={<ThankYou />} />
         <Route path="/portfolio" element={<Portfolio />} />

        </Routes>

<footer className="bg-primary text-white py-16">
  <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-10 text-center md:text-left">
    <div>
      <p className="text-2xl font-serif mb-2">Tessa Flora</p>
      <p className="text-sm opacity-75">Fresh flowers with love</p>
    </div>

    <div>
      <h4 className="font-medium mb-4">Quick Links</h4>
      <div className="space-y-2 text-sm">
        <Link to="/about" className="block hover:underline">About Us</Link>
        <Link to="/shop" className="block hover:underline">Shop</Link>
        <Link to="/contact" className="block hover:underline">Contact</Link>
      </div>
    </div>

    <div>
      <h4 className="font-medium mb-4">Legal</h4>
      <div className="space-y-2 text-sm">
        <a href="https://drive.google.com/file/d/157DPFIzM2rG51kR9umtnuM2_wUb_EeSs/view" target="_blank" rel="noopener noreferrer" className="block hover:underline">Privacy Policy</a>
        <a href="https://drive.google.com/file/d/1ErBJ_SQK4efxBbSsNrQ7sx0wFMOcECV_/view" target="_blank" rel="noopener noreferrer" className="block hover:underline">Terms & Conditions</a>
      </div>
    </div>

    <div>
      <h4 className="font-medium mb-4">Follow Us</h4>
     <a 
  href="https://www.instagram.com/tessaflora_/" 
  target="_blank" 
  rel="noopener noreferrer"
  className="inline-flex items-center gap-3 hover:underline text-lg"
>
  <img 
    src="/static/instagram.svg" 
    alt="Instagram" 
    className="w-5 h-5"
  />
  Instagram
</a>
    </div>
  </div>

  <div className="text-center text-sm opacity-75 mt-12">
    © {new Date().getFullYear()} Tessa Flora
  </div>
</footer>
      </div>
    </Router>
  );
}

export default App;